import axios from 'axios';

export enum ExportFileType {
  DeprecatedCsv = 'deprecated_csv',
  Csv = 'csv',
  Zip = 'zip',
}

export interface ExportResponse {
  // New flat format (direct from backend)
  fileType?: ExportFileType | string;
  exportId?: string;
  filename?: string;
  exportIds?: string[];
  metadata?: {
    composedFileCount?: number;
  };

  // Legacy nested format (for backward compatibility)
  exportResult?: {
    fileType: ExportFileType;
    exportIds?: string[];
    filename?: string;
    metadata?: {
      composedFileCount?: number;
    };
  };
}

export interface ExportDownloadInfo {
  type: 'backcompat' | 'single_csv' | 'single_zip';
  downloads: Array<{
    url: string;
    filename?: string;
  }>;
}

/**
 * Processes export response and returns download information
 * Handles three formats:
 * 1. New zip format: exportResult with fileType 'zip' (single_zip)
 * 2. New CSV format: exportResult with fileType 'csv' (single_csv)
 * 3. Legacy format: exportIds[] or deprecated_csv (backcompat)
 */
export function processExportResponse(response: ExportResponse, baseUrl: string, orgId: string): ExportDownloadInfo {
  // Handle new flat format with exportId + fileType
  if (response.exportId && response.fileType) {
    if (response.fileType === 'zip') {
      const url = `${baseUrl}v2/orgs/${orgId}/exports/${response.exportId}?exportType=${response.fileType}&rawUrl=true`;
      console.log('zip url', url);
      return {
        type: 'single_zip',
        downloads: [
          {
            url,
            filename: response.filename,
          },
        ],
      };
    } else if (response.fileType === 'csv') {
      const url = `${baseUrl}v2/orgs/${orgId}/exports/${response.exportId}?exportType=csv&rawUrl=true`;
      console.log('csv url', url);
      return {
        type: 'single_csv',
        downloads: [
          {
            url,
            filename: response.filename,
          },
        ],
      };
    }
  }

  // Handle legacy csvs_deprecated format
  if (response.fileType === 'csvs_deprecated' && response.exportIds) {
    return {
      type: 'backcompat',
      downloads: response.exportIds.map((exportId) => ({
        url: `${baseUrl}v2/orgs/${orgId}/exports/${exportId}?rawUrl=true`,
      })),
    };
  }

  // Handle legacy nested format for backward compatibility
  if (response.exportResult) {
    const { fileType } = response.exportResult;

    if (fileType === ExportFileType.Zip && response.exportResult.filename) {
      return {
        type: 'single_zip',
        downloads: [
          {
            url: `${baseUrl}v2/orgs/${orgId}/exports/${response.exportResult.filename}`,
            filename: response.exportResult.filename,
          },
        ],
      };
    } else if (fileType === ExportFileType.Csv && response.exportResult.filename) {
      return {
        type: 'single_csv',
        downloads: [
          {
            url: `${baseUrl}v2/orgs/${orgId}/exports/${response.exportResult.filename}`,
            filename: response.exportResult.filename,
          },
        ],
      };
    } else if (fileType === ExportFileType.DeprecatedCsv && response.exportResult.exportIds) {
      return {
        type: 'backcompat',
        downloads: response.exportResult.exportIds.map((exportId) => ({
          url: `${baseUrl}v2/orgs/${orgId}/exports/${exportId}`,
        })),
      };
    }
  }

  // Handle legacy exportIds format
  if (response.exportIds) {
    return {
      type: 'backcompat',
      downloads: response.exportIds.map((exportId) => ({
        url: `${baseUrl}v2/orgs/${orgId}/exports/${exportId}`,
      })),
    };
  }

  // No valid export data found
  return {
    type: 'backcompat',
    downloads: [],
  };
}

/**
 * Downloads files based on export response
 * For CSV files, gets download URLs first, then downloads
 * For zip files, downloads directly
 */
export async function downloadExportFiles(
  response: ExportResponse,
  baseUrl: string,
  orgId: string,
  downloadFile: (url: string) => void,
  chunkedExportEnabled?: boolean
): Promise<void> {
  const downloadInfo = processExportResponse(response, baseUrl, orgId);

  if (downloadInfo.type === 'single_zip') {
    // Get signed download URLs for zip files first
    const downloadUrlPromises = downloadInfo.downloads.map((download) =>
      axios.get(download.url, { withCredentials: true })
    );

    try {
      const downloadUrls = await Promise.all(downloadUrlPromises);
      downloadUrls.forEach((urlResponse: any) => {
        if (urlResponse.status === 200 && urlResponse.data) {
          downloadFile(urlResponse.data);
        }
      });
    } catch (error) {
      console.error('Failed to get ZIP download URLs:', error);
      throw error;
    }
  } else {
    // For CSV files (backcompat, single_csv), get download URLs first
    const downloadUrlPromises = downloadInfo.downloads.map((download) =>
      axios.get(download.url, { withCredentials: true })
    );

    try {
      const downloadUrls = await Promise.all(downloadUrlPromises);
      downloadUrls.forEach((urlResponse: any) => {
        if (urlResponse.status === 200 && urlResponse.data) {
          downloadFile(urlResponse.data);
        }
      });
    } catch (error) {
      console.error('Error getting download URLs:', error);
      throw error;
    }
  }
}
